import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const update = (k) => (e) => {
    setForm((prev) => ({ ...prev, [k]: e.target.value }));
    if (k === "phone" && phoneError) setPhoneError("");
  };

  const validatePhone = (phone) => {
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) {
      return true;
    }
    return digits.length === 10;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validatePhone(form.phone)) {
      const err = "Please enter a valid 10-digit mobile number";
      setPhoneError(err);
      toast.error(err);
      return;
    }
    setPhoneError("");
    setLoading(true);
    try {
      const payload = {
        full_name: form.name,
        email: form.email,
        mobile_number: form.phone,
        message: form.message,
        subject: form.subject
      };
      await submitContact(payload);
      toast.success("Message received. We'll get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-contact">
      <section className="relative bg-[color:var(--paper-2)] overflow-hidden">
        <div className="absolute inset-0 bg-grid-light opacity-40" />
        <div className="relative container-x pt-4 md:pt-6 lg:pt-8 pb-12">
          <SectionHeader
            eyebrow="Get in touch"
            title="Let's talk about"
            titleAccent="connecting your healthcare."
            subtitle="Sales, partnerships, integrations or careers — drop us a line and the right human will reply within a business day."
          />
        </div>
      </section>

      <section className="pb-20 md:pb-28 pt-8 md:pt-12 bg-white">
        <div className="container-x max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 space-y-5">
              {[
                { icon: Mail, t: "Email", v: "info@saaluvars.com", href: "mailto:info@saaluvars.com" },
                { icon: Phone, t: "Phone", v: "+91 9600043880", href: "tel:+919600043880" },
                { icon: MapPin, t: "Office", v: "Chennai" },
                // { icon: MessageSquare, t: "Live Chat", v: "Available Mon–Sat, 9am–9pm IST" },
              ].map((c, i) => (
                <div key={c.t} className="flex gap-5 items-center rounded-3xl border border-slate-200 p-6 md:p-8 hover:shadow-lg transition-shadow bg-slate-50/50" data-testid={`contact-info-${i + 1}`}>
                  <div className="w-14 h-14 rounded-2xl bg-[color:var(--brand-soft)] grid place-items-center text-[color:var(--brand)] flex-shrink-0">
                    <c.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">{c.t}</div>
                    {c.href ? (
                      <a href={c.href} className="text-lg md:text-xl font-bold text-slate-900 hover:text-[color:var(--brand)] transition">{c.v}</a>
                    ) : (
                      <div className="text-lg md:text-xl font-bold text-slate-900">{c.v}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-7">
              <form onSubmit={submit} className="rounded-3xl border border-slate-200 p-7 md:p-10 bg-white space-y-5 h-full" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Full Name" required>
                    <input required value={form.name} onChange={update("name")} className="field" placeholder="Dr. Karunakaran S" data-testid="contact-name" />
                  </Field>
                  <Field label="Email" required>
                    <input required type="email" value={form.email} onChange={update("email")} className="field" placeholder="you@hospital.com" data-testid="contact-email" />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Phone" required>
                    <input
                      required
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      className={`field ${phoneError ? "!border-red-500 ring-2 ring-red-100" : ""}`}
                      placeholder="9876543210"
                      data-testid="contact-phone"
                    />
                    {phoneError && <p className="text-xs text-red-500 mt-1 font-medium">{phoneError}</p>}
                  </Field>
                  <Field label="Subject">
                    <input value={form.subject} onChange={update("subject")} className="field" placeholder="Partnership / Sales / Support" data-testid="contact-subject" />
                  </Field>
                </div>
                <Field label="Message" required>
                  <textarea required rows={6} value={form.message} onChange={update("message")} className="field resize-none" placeholder="Tell us about your facility and what you'd like to solve…" data-testid="contact-message" />
                </Field>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">We typically respond within 24 hours.</p>
                  <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">
                    {loading ? "Sending…" : "Send Message"} <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .field {
          width: 100%;
          padding: 14px 16px;
          border-radius: var(--radius);
          border: 1px solid #E2E8F0;
          background: #fff;
          font-size: 14px;
          color: #0F172A;
          transition: all .2s;
        }
        .field:focus { outline: none; border-color: #E94B2C; box-shadow: 0 0 0 4px rgba(233,75,44,0.12); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-700 font-bold">{label}{required && <span className="text-[color:var(--brand)]"> *</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
